import type { Dayjs } from '@/config/dayjs';
import type {
  WealthTrendPoint,
  WealthTrendSeries,
} from '@/interfaces/wealthReportedTrend';
import { parseAppDate } from '@/utils/date';

/** The days a chart or a Net Change is measured over, inclusive. */
export interface WealthTrendWindow {
  start: Dayjs;
  end: Dayjs;
}

/** A dollar move and the percentage it is of the opening value. */
export interface WealthTrendChange {
  dollarChange: number;
  /** Null when the window opened at zero: there is no base to measure against. */
  percentChange: number | null;
}

/** A point's day, or null when the API sent something that is not a date. */
export function pointDay(point: WealthTrendPoint): Dayjs | null {
  return parseAppDate(point.Date)?.startOf('day') ?? null;
}

/**
 * Where the popover's window starts for a hovered day, as legacy's
 * `HistoricalTrend.buildTipParams` sets it:
 *
 * - the 1st of a month looks back to the start of the previous month;
 * - the last day of a month looks back to the start of that month;
 * - any other day looks back 30 days.
 */
export function comparisonDateFor(hovered: Dayjs): Dayjs {
  const day = hovered.startOf('day');
  if (day.isSame(day.startOf('month'), 'day')) {
    return day.subtract(1, 'day').startOf('month');
  }
  if (day.isSame(day.endOf('month'), 'day')) {
    return day.startOf('month');
  }
  return day.subtract(30, 'day');
}

function isWithin(day: Dayjs, window: WealthTrendWindow): boolean {
  return !day.isBefore(window.start, 'day') && !day.isAfter(window.end, 'day');
}

/** A series' first and last points inside the window, by day. */
function endpoints(
  series: WealthTrendSeries,
  window: WealthTrendWindow | null,
): [WealthTrendPoint, WealthTrendPoint] | null {
  const dated = series.data
    .flatMap((point) => {
      const day = pointDay(point);
      if (!day) return [];
      if (window && !isWithin(day, window)) return [];
      return [{ point, day }];
    })
    .sort((a, b) => a.day.valueOf() - b.day.valueOf());

  if (dated.length === 0) return null;
  return [dated[0].point, dated[dated.length - 1].point];
}

/**
 * The combined change across every series over the window: each series' last
 * point less its first, summed. Without a window, the whole response counts.
 *
 * Measured from the points that exist rather than from the exact window
 * dates. Legacy matched the exact start date, so a range starting on a
 * weekend or holiday -- a day with no balances -- compared against zero and
 * reported the whole book as new money.
 *
 * Every series counts whether or not it is shown; hiding a line in the legend
 * does not change the figure.
 */
export function trendNetChange(
  series: readonly WealthTrendSeries[],
  window: WealthTrendWindow | null = null,
): number {
  return series.reduce((sum, line) => {
    const ends = endpoints(line, window);
    if (!ends) return sum;
    const [first, last] = ends;
    return sum + (last.Value - first.Value);
  }, 0);
}

/** The move from `beginning` to `end`, for the popover's Change line. */
export function windowChange(
  beginning: number,
  end: number,
): WealthTrendChange {
  const dollarChange = end - beginning;
  if (beginning === 0) return { dollarChange, percentChange: null };
  return {
    dollarChange,
    percentChange: (dollarChange / Math.abs(beginning)) * 100,
  };
}
